import { RpcError } from "livekit-client";

export const SCREEN_SHARE_LEASE_METHOD = "com.memotivation.screenshare.lease.v1";
export const SCREEN_SHARE_STOP_METHOD = "com.memotivation.screenshare.stop.v1";
export const SCREEN_SHARE_LEASE_MS = 20000;
export const SCREEN_SHARE_PUBLISH_BUDGET_MS = 6000;
export const SCREEN_SHARE_RPC_TIMEOUT_MS = 15000;
export const SCREEN_SHARE_MEDIA_TIMEOUT_MS = 5000;
export const SCREEN_SHARE_POLL_MS = 100;

export const SCREEN_SHARE_CONFIRMATION = "Other participants will see the screen you select. " +
    "If another device is sharing, continuing will end its screen share. Would you like to continue?";
export const SCREEN_SHARE_TAKEOVER_CONFIRMATION = "Taking over will end the current screen share from another device. " +
    "Other participants will then see the screen you select. Would you like to take over?";
export const SCREEN_SHARE_FAILURE_MESSAGE = "Unable to start or take over screen sharing. Please try again.";
export const SCREEN_SHARE_TAKEN_OVER_MESSAGE = "Another participant has taken over screen sharing.";

export type ScreenPublication = { deviceIdentity: string; trackSid: string };
export type ScreenShareLease = {
    requestId: string;
    holderIdentity: string;
    leaderIdentity: string;
    expiresAt: number;
};
type Envelope = { v: number; room_id: string; request_id: string };
type Payload = Envelope & {
    action?: string;
    status?: string;
    lease_ms?: number;
    track_sid?: string;
    new_owner?: string;
};
export type ScreenShareTransport = {
    localIdentity: () => string;
    joinedIdentities: () => string[];
    publications: () => ScreenPublication[];
    register: (method: string, handler: (caller: string, payload: string) => Promise<string>) => void;
    unregister: (method: string) => void;
    invoke: (identity: string, method: string, payload: string) => Promise<string>;
};
type HandoverOptions = {
    transport: ScreenShareTransport;
    roomId: string;
    clock: () => number;
    isCurrent: () => boolean;
    stopLocal: (trackSid: string, newOwner: string, leader: string) => Promise<void>;
};

export const waitForScreenShare = (milliseconds: number): Promise<void> =>
    new Promise(resolve => setTimeout(resolve, milliseconds));

// The underlying RPC can complete after this timeout. Its reservation must still expire normally.
export const boundScreenShareWait = async <T>(operation: Promise<T>, milliseconds: number): Promise<T> => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
        return await Promise.race([
            operation,
            new Promise<never>((_, reject) => {
                timeout = setTimeout(() => reject(new RpcError(2003, "Screen share transfer timed out")), milliseconds);
            })
        ]);
    } finally {
        clearTimeout(timeout);
    }
};

/** Coordinates device ownership only; capture and publication belong to the session service. */
export class ScreenShareHandover {
    private reservation: ScreenShareLease | null = null;
    private grantTail: Promise<void> = Promise.resolve();
    private closed = false;
    private readonly transport: ScreenShareTransport;
    private readonly roomId: string;
    private readonly clock: () => number;
    private readonly isCurrent: () => boolean;
    private readonly stopLocal: HandoverOptions["stopLocal"];

    constructor(options: HandoverOptions) {
        this.transport = options.transport;
        this.roomId = options.roomId;
        this.clock = options.clock;
        this.isCurrent = options.isCurrent;
        this.stopLocal = options.stopLocal;
    }

    start(): void {
        this.transport.register(SCREEN_SHARE_LEASE_METHOD, (caller, payload) => this.receiveLease(caller, payload));
        this.transport.register(SCREEN_SHARE_STOP_METHOD, (caller, payload) => this.receiveStop(caller, payload));
    }

    close(): void {
        this.closed = true;
        this.reservation = null;
        this.transport.unregister(SCREEN_SHARE_LEASE_METHOD);
        this.transport.unregister(SCREEN_SHARE_STOP_METHOD);
    }

    leaderIdentity(): string {
        this.requireCurrent();
        const identities = this.transport.joinedIdentities();
        // Default sorting compares UTF-16 code units, matching Kotlin String ordering.
        identities.sort();
        if (!identities.length) {
            throw new RpcError(2002, "Empty call");
        }
        return identities[0];
    }

    async acquire(requestId: string): Promise<ScreenShareLease> {
        const leaderIdentity = this.leaderIdentity();
        const holderIdentity = this.transport.localIdentity();
        const expiresAt = this.clock() + SCREEN_SHARE_LEASE_MS;
        const payload = JSON.stringify({ ...this.envelope(requestId), action: "claim" });
        const reply = await boundScreenShareWait(
            this.invokeLease(leaderIdentity, holderIdentity, payload), SCREEN_SHARE_RPC_TIMEOUT_MS
        );
        const grant = this.parse(leaderIdentity, reply);
        if (grant.request_id !== requestId || grant.status !== "granted" || grant.lease_ms !== SCREEN_SHARE_LEASE_MS) {
            throw new RpcError(2002, "Invalid share grant");
        }
        const lease = { requestId, holderIdentity, leaderIdentity, expiresAt };
        try {
            // RPC responses and room updates arrive independently. The leader may see
            // removal before this device receives the same unpublish notification.
            await this.waitForPublicationRemoval(lease);
            this.verify(lease, SCREEN_SHARE_PUBLISH_BUDGET_MS);
            return lease;
        } catch (error) {
            // The caller has not received the lease yet, so it cannot clean up this
            // reservation itself. Release it here before allowing another attempt.
            await this.cancel(lease).catch(cancelError => {
                console.warn("Unable to cancel rejected screen share grant.", cancelError);
            });
            throw error;
        }
    }

    verify(lease: ScreenShareLease, minimumRemaining: number): void {
        this.verifyLease(lease, minimumRemaining);
        if (this.transport.publications().length) {
            throw new RpcError(2002, "An existing screen share has not stopped");
        }
    }

    verifyPublished(lease: ScreenShareLease): void {
        this.verifyLease(lease, 0);
        const publications = this.transport.publications();
        if (!publications.some(item => item.deviceIdentity === lease.holderIdentity) ||
            publications.some(item => item.deviceIdentity !== lease.holderIdentity)) {
            throw new RpcError(2002, "Share reservation changed");
        }
    }

    async cancel(lease: ScreenShareLease): Promise<void> {
        if (this.closed || !this.isCurrent()) {
            return;
        }
        const payload = JSON.stringify({ ...this.envelope(lease.requestId), action: "cancel" });
        await boundScreenShareWait(
            this.invokeLease(lease.leaderIdentity, lease.holderIdentity, payload), SCREEN_SHARE_RPC_TIMEOUT_MS
        );
    }

    private async waitForPublicationRemoval(lease: ScreenShareLease): Promise<void> {
        const deadline = this.clock() + SCREEN_SHARE_MEDIA_TIMEOUT_MS;
        while (this.transport.publications().length) {
            this.verifyLease(lease, SCREEN_SHARE_PUBLISH_BUDGET_MS);
            if (this.clock() >= deadline) {
                throw new RpcError(2003, "Existing screen share did not disappear locally");
            }
            await waitForScreenShare(SCREEN_SHARE_POLL_MS);
        }
    }

    private verifyLease(lease: ScreenShareLease, minimumRemaining: number): void {
        if (lease.leaderIdentity !== this.leaderIdentity() ||
            lease.holderIdentity !== this.transport.localIdentity() ||
            lease.expiresAt - this.clock() <= minimumRemaining) {
            throw new RpcError(2002, "Share reservation changed");
        }
    }

    private invokeLease(leader: string, caller: string, payload: string): Promise<string> {
        if (leader === this.transport.localIdentity()) {
            return this.receiveLease(caller, payload);
        }
        return this.transport.invoke(leader, SCREEN_SHARE_LEASE_METHOD, payload);
    }

    private async receiveLease(caller: string, payload: string): Promise<string> {
        const previous = this.grantTail;
        let release: () => void = () => undefined;
        this.grantTail = new Promise(resolve => { release = resolve; });
        await previous;
        try {
            const request = this.parse(caller, payload);
            if (this.leaderIdentity() !== this.transport.localIdentity()) {
                throw new RpcError(2002, "Share leader changed");
            }
            if (request.action === "cancel") {
                this.clearReservation(request.request_id, caller);
                return this.response(request.request_id, "cancelled");
            }
            if (request.action !== "claim") {
                throw new RpcError(2002, "Unknown share action");
            }
            return await this.grant(caller, request.request_id);
        } finally {
            release();
        }
    }

    private async grant(caller: string, requestId: string): Promise<string> {
        if (this.transport.publications().some(item => item.deviceIdentity === this.reservation?.holderIdentity)) {
            this.reservation = null;
        }
        if (this.reservation && this.reservation.expiresAt > this.clock()) {
            throw new RpcError(2001, "A share is already starting");
        }
        this.reservation = {
            requestId, holderIdentity: caller, leaderIdentity: this.transport.localIdentity(),
            expiresAt: this.clock() + SCREEN_SHARE_LEASE_MS
        };
        try {
            for (const publication of this.transport.publications()) {
                this.requireMembership(caller);
                if (publication.deviceIdentity === caller) {
                    throw new RpcError(2001, "Caller is already sharing");
                }
                const payload = JSON.stringify({
                    ...this.envelope(requestId), track_sid: publication.trackSid, new_owner: caller
                });
                const reply = publication.deviceIdentity === this.transport.localIdentity()
                    ? await this.receiveStop(this.transport.localIdentity(), payload)
                    : await boundScreenShareWait(
                        this.transport.invoke(publication.deviceIdentity, SCREEN_SHARE_STOP_METHOD, payload),
                        SCREEN_SHARE_RPC_TIMEOUT_MS
                    );
                const stopped = this.parse(publication.deviceIdentity, reply);
                if (stopped.request_id !== requestId || stopped.status !== "stopped") {
                    throw new RpcError(2002, "Invalid stop acknowledgement");
                }
            }
            const deadline = this.clock() + SCREEN_SHARE_MEDIA_TIMEOUT_MS;
            while (this.transport.publications().length) {
                this.requireCurrent();
                if (this.clock() >= deadline) {
                    throw new RpcError(2003, "Existing screen share did not stop");
                }
                await waitForScreenShare(SCREEN_SHARE_POLL_MS);
            }
            this.requireMembership(caller);
            if (this.leaderIdentity() !== this.transport.localIdentity()) {
                throw new RpcError(2002, "Share leader changed");
            }
            return JSON.stringify({ ...this.envelope(requestId), status: "granted", lease_ms: SCREEN_SHARE_LEASE_MS });
        } catch (error) {
            this.clearReservation(requestId, caller);
            throw error;
        }
    }

    private async receiveStop(caller: string, payload: string): Promise<string> {
        const request = this.parse(caller, payload);
        if (typeof request.new_owner !== "string" || typeof request.track_sid !== "string" || !request.track_sid) {
            throw new RpcError(2002, "Invalid share transfer");
        }
        this.requireMembership(request.new_owner);
        if (caller !== this.leaderIdentity()) {
            throw new RpcError(2002, "Invalid share leader");
        }
        const screen = this.transport.publications().find(item =>
            item.deviceIdentity === this.transport.localIdentity() && item.trackSid === request.track_sid
        );
        if (screen) {
            await this.stopLocal(request.track_sid, request.new_owner, caller);
        }
        return this.response(request.request_id, "stopped");
    }

    private parse(caller: string, payload: string): Payload {
        this.requireMembership(caller);
        try {
            if (new TextEncoder().encode(payload).byteLength > 4096) {
                throw new Error("Payload too large");
            }
            const request: Payload = JSON.parse(payload);
            if (!request || request.v !== 1 || request.room_id !== this.roomId ||
                typeof request.request_id !== "string" ||
                !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(request.request_id)) {
                throw new Error("Invalid envelope");
            }
            return request;
        } catch {
            throw new RpcError(2002, "Invalid share payload");
        }
    }

    private requireCurrent(): void {
        if (this.closed || !this.isCurrent()) {
            throw new RpcError(2002, "Call is no longer connected");
        }
    }

    private requireMembership(identity: string): void {
        this.requireCurrent();
        if (!this.transport.joinedIdentities().includes(identity)) {
            throw new RpcError(2002, "Share participant left");
        }
    }

    private clearReservation(requestId: string, holder: string): void {
        if (this.reservation?.requestId === requestId && this.reservation.holderIdentity === holder) {
            this.reservation = null;
        }
    }

    private envelope(requestId: string): Envelope {
        return { v: 1, room_id: this.roomId, request_id: requestId };
    }

    private response(requestId: string, status: string): string {
        return JSON.stringify({ ...this.envelope(requestId), status });
    }
}
